// Movimento do site: rolagem suave (Lenis, só no computador), parallax do hero, revelações ao
// rolar, contadores e cards da listagem. Microinterações de hover ficam só no CSS (_motion.scss).
//
// Sistema de atributos (qualquer template pode usar direto no HTML):
//   data-reveal="subir" | "linhas" | "fade"   entra quando aparece na tela
//   data-reveal-atraso="0.2"                  atraso, em segundos
//   data-stagger                              os filhos entram em sequência
//   data-count                                o primeiro número do texto conta a partir de 0
//   data-parallax="0.1"                       desloca ao rolar (fração da própria altura)
// As seções que já existiam recebem esses atributos pelo mapa AUTOMATICO, abaixo.
//
// Acessibilidade e desempenho: nada roda com "reduzir movimento" ligado no sistema; as animações
// mexem só em transform, opacity e máscaras (sem mudar o layout); o CSS (_motion.scss) só esconde
// o que vai entrar animado quando o <html> tem a classe "motion-ativo" (header.php).

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import { Flip } from 'gsap/Flip';
import Lenis from 'lenis';

gsap.registerPlugin(ScrollTrigger, SplitText, Flip);

const EASE = 'expo.out';
const INICIO = 'top 88%';

// Seletor => atributos aplicados (seções existentes, sem espalhar data-* pelos templates).
const AUTOMATICO = [
    ['.home-secao__titulo, .home-institucional__texto', { reveal: 'linhas' }],
    ['.home-secao__subtitulo, .home-secao__link, .home-institucional__acoes, .cta-card, .home-bairros__todos', { reveal: 'subir' }],
    ['.home-imoveis .imoveis-resultados, .home-lancamentos__trilho, .home-tipos__lista, .home-bairros__lista, .home-blog__lista', { stagger: '' }],
    ['.tipo-card__total, .bairro-card__total', { count: '' }],
    ['.home-institucional__marca', { parallax: '0.12' }],
    ['.imovel-bloco, .imovel-pagina__lateral .sticky-top > *, .secao-relacionados__rotulo', { reveal: 'subir' }],
    ['.secao-relacionados .imoveis-resultados', { stagger: '' }],
    ['.site-footer__coluna', { reveal: 'subir' }],
];

// Quebras de linha só podem ser medidas com a fonte final carregada (senão o texto "pula" no fim).
// Limite de 0,8s: com fonte lenta, anima com o que houver.
const fontesProntas = () => Promise.race([
    document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve(),
    new Promise((resolver) => window.setTimeout(resolver, 800)),
]);

const reduzido = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const temMouse = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;

// Rolagem suave ---------------------------------------------------------------------------------

function iniciarLenis() {
    if (!temMouse()) {
        return null;
    }
    const lenis = new Lenis({
        autoRaf: false,
        anchors: { offset: -120 },
        // Listas e painéis com rolagem própria continuam com a rolagem nativa.
        prevent: (no) => Boolean(no.closest && no.closest('.sugestoes-busca, .select-v2__lista, .filtros-modal, .filtros-bairro__sugestoes, .menu-mobile, .fancybox__container')),
    });
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add((tempo) => lenis.raf(tempo * 1000));
    gsap.ticker.lagSmoothing(0);

    // Modal de filtros, menu e galeria em tela cheia travam a página: a rolagem suave para junto.
    const travado = () => document.body.classList.contains('filtros-aberto')
        || document.body.classList.contains('menu-mobile-aberto')
        || document.documentElement.classList.contains('with-fancybox');
    const alternar = () => (travado() ? lenis.stop() : lenis.start());
    const observador = new MutationObserver(alternar);
    observador.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    observador.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return lenis;
}

// Hero da home ----------------------------------------------------------------------------------

// A entrada do hero é só CSS (_motion.scss), para não atrasar a primeira tela; aqui fica o parallax
// da foto ao rolar para fora do hero.
function iniciarHero() {
    const hero = document.querySelector('.home-hero');
    const foto = hero && hero.querySelector('.home-hero__foto');
    if (!foto) {
        return;
    }
    gsap.fromTo(foto, { yPercent: 0 }, {
        yPercent: 8,
        ease: 'none',
        scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true },
    });
}

// Revelações ------------------------------------------------------------------------------------

function aplicarAutomatico() {
    AUTOMATICO.forEach(([seletor, atributos]) => {
        document.querySelectorAll(seletor).forEach((el) => {
            Object.entries(atributos).forEach(([chave, valor]) => {
                if (el.dataset[chave] === undefined) {
                    el.dataset[chave] = valor;
                }
            });
        });
    });
}

function revelar(el) {
    const tipo = el.dataset.reveal || 'subir';
    const atraso = parseFloat(el.dataset.revealAtraso) || 0;
    const gatilho = { trigger: el, start: INICIO, once: true };

    if (tipo === 'linhas') {
        fontesProntas().then(() => revelarLinhas(el, atraso, gatilho));
        return;
    }
    const de = tipo === 'fade' ? { opacity: 0 } : { opacity: 0, y: 40 };
    gsap.fromTo(el, de, {
        opacity: 1,
        y: 0,
        duration: 1,
        ease: EASE,
        delay: atraso,
        scrollTrigger: gatilho,
        clearProps: 'transform',
    });
}

function revelarLinhas(el, atraso, gatilho) {
    const split = SplitText.create(el, { type: 'lines', mask: 'lines', aria: 'none' });
    gsap.fromTo(split.lines, { yPercent: 110 }, {
        yPercent: 0,
        duration: 1.1,
        ease: EASE,
        stagger: 0.08,
        delay: atraso,
        scrollTrigger: gatilho,
        // Depois de entrar, volta ao texto normal (quebra de linha livre ao redimensionar).
        onComplete: () => split.revert(),
    });
}

function revelarEmSequencia(container) {
    const filhos = [...container.children];
    if (!filhos.length) {
        return;
    }
    gsap.fromTo(filhos, { opacity: 0, y: 40 }, {
        opacity: 1,
        y: 0,
        duration: 0.9,
        ease: EASE,
        // Listas longas (ex.: 39 bairros) não podem demorar: a sequência inteira dura no máximo ~0,6s.
        stagger: Math.min(0.08, 0.6 / filhos.length),
        scrollTrigger: { trigger: container, start: INICIO, once: true },
        clearProps: 'transform',
    });
}

// Conta do 0 até o primeiro número do texto ("248 imóveis", "355 imóveis encontrados").
function contar(el) {
    const caminhante = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    let no;
    while ((no = caminhante.nextNode())) {
        const achado = no.nodeValue.match(/\d[\d.]*/);
        if (!achado) {
            continue;
        }
        const alvo = parseInt(achado[0].replace(/\./g, ''), 10);
        if (!alvo || alvo < 2) {
            return;
        }
        const antes = no.nodeValue.slice(0, achado.index);
        const depois = no.nodeValue.slice(achado.index + achado[0].length);
        const comPonto = achado[0].includes('.');
        const texto = no;
        const valor = { atual: 0 };
        gsap.to(valor, {
            atual: alvo,
            duration: 1.4,
            ease: 'power2.out',
            scrollTrigger: { trigger: el, start: 'top 95%', once: true },
            onUpdate: () => {
                const n = Math.round(valor.atual);
                texto.nodeValue = antes + (comPonto ? n.toLocaleString('pt-BR') : n) + depois;
            },
        });
        return;
    }
}

function parallax(el) {
    const quanto = parseFloat(el.dataset.parallax) || 0.1;
    gsap.fromTo(el, { yPercent: -quanto * 100 }, {
        yPercent: quanto * 100,
        ease: 'none',
        scrollTrigger: { trigger: el.closest('section') || el, start: 'top bottom', end: 'bottom top', scrub: true },
    });
}

function iniciarRevelacoes(raiz = document) {
    raiz.querySelectorAll('[data-reveal]').forEach(revelar);
    raiz.querySelectorAll('[data-stagger]').forEach(revelarEmSequencia);
    raiz.querySelectorAll('[data-count]').forEach(contar);
    if (window.matchMedia('(min-width: 768px)').matches) {
        raiz.querySelectorAll('[data-parallax]').forEach(parallax);
    }
}

// Cards da listagem (escondidos pelo CSS até entrar) ----------------------------------------------

function revelarCards(cards) {
    ScrollTrigger.batch(cards, {
        start: 'top 95%',
        once: true,
        onEnter: (lote) => gsap.fromTo(lote, { opacity: 0, y: 40 }, {
            opacity: 1,
            y: 0,
            duration: 0.9,
            ease: EASE,
            stagger: 0.08,
            clearProps: 'transform',
        }),
    });
}

// Os cards que vêm com a página entram por CSS (_motion.scss); os do carregamento automático
// (busca-imoveis.js) entram aqui, em sequência, conforme aparecem na tela.
function iniciarCards() {
    document.addEventListener('ni:novos-imoveis', (e) => {
        e.detail.forEach((card) => card.classList.add('motion-js'));
        revelarCards(e.detail);
        ScrollTrigger.refresh();
    });
}

// Texto de exemplo "digitado" nos campos de busca ---------------------------------------------------
// Ensina o que a busca entende sem texto explicativo. Pausa com o campo em foco, preenchido ou fora
// da tela; mexe só no placeholder (nada de layout).
function placeholderAnimado(campo, exemplos) {
    const original = campo.getAttribute('placeholder') || '';
    let visivel = false;
    let timer = null;
    // Se o texto inicial já é o primeiro exemplo, o ciclo começa pelo segundo.
    let indice = exemplos[0] === original ? 1 : 0;

    const parado = () => !visivel || document.activeElement === campo || campo.value !== '' || document.hidden;
    const esperarEm = (ms, fn) => {
        timer = window.setTimeout(fn, ms);
    };

    function apagar(texto, depois) {
        if (parado()) {
            return pausar();
        }
        if (!texto) {
            return depois();
        }
        campo.setAttribute('placeholder', texto.slice(0, -1));
        esperarEm(28, () => apagar(texto.slice(0, -1), depois));
    }

    function digitar(alvo, parcial, depois) {
        if (parado()) {
            return pausar();
        }
        if (parcial === alvo) {
            return depois();
        }
        const proximo = alvo.slice(0, parcial.length + 1);
        campo.setAttribute('placeholder', proximo);
        esperarEm(55 + Math.random() * 45, () => digitar(alvo, proximo, depois));
    }

    function ciclo() {
        const exemplo = exemplos[indice % exemplos.length];
        indice += 1;
        apagar(campo.getAttribute('placeholder'), () => digitar(exemplo, '', () => esperarEm(1700, ciclo)));
    }

    function pausar() {
        window.clearTimeout(timer);
        timer = null;
        campo.setAttribute('placeholder', original);
    }

    function retomar() {
        if (!timer && !parado()) {
            esperarEm(1500, ciclo);
        }
    }

    new IntersectionObserver((entradas) => {
        visivel = entradas.some((entrada) => entrada.isIntersecting);
        visivel ? retomar() : pausar();
    }).observe(campo);
    campo.addEventListener('focus', pausar);
    campo.addEventListener('blur', retomar);
    campo.addEventListener('input', () => (campo.value ? pausar() : retomar()));
    document.addEventListener('visibilitychange', () => (document.hidden ? pausar() : retomar()));
}

function iniciarPlaceholders() {
    const hero = document.querySelector('.home-busca input[name="local"]');
    if (hero) {
        // Só bairros (os das buscas rápidas); o texto inicial do campo já é o primeiro deles.
        const bairros = [...document.querySelectorAll('.home-busca__rapidas a')].map((a) => a.textContent.trim());
        if (bairros.length > 1) {
            placeholderAnimado(hero, bairros);
        }
    }
    document.querySelectorAll('.busca-compacta input[name="q"]').forEach((campo) => {
        placeholderAnimado(campo, ['Apartamento em Manaíra', 'Casa no Bessa', 'Cabo Branco', 'Lançamentos', 'Cobertura em Intermares']);
    });
}

// Troca grade/lista (busca-imoveis.js chama window.niMotion.trocarLayout) ---------------------------
// Os cards visíveis e suas fotos vão da posição antiga para a nova (Flip); os textos reaparecem
// logo depois. Os que estão fora da tela trocam direto.
function trocarLayout(container, alterar) {
    const perto = (el) => {
        const r = el.getBoundingClientRect();
        return r.bottom > -200 && r.top < window.innerHeight + 200;
    };
    const cards = [...container.querySelectorAll('.imovel-card')].filter(perto);
    if (!cards.length) {
        alterar();
        return;
    }
    const alvos = cards.flatMap((card) => [card, card.querySelector('.imovel-card__galeria')].filter(Boolean));
    const estado = Flip.getState(alvos);
    alterar();
    Flip.from(estado, {
        targets: alvos,
        duration: 0.7,
        ease: 'power3.inOut',
        nested: true,
        scale: false,
        stagger: 0.015,
        onComplete: () => ScrollTrigger.refresh(),
    });
    // Textos, preço e botões somem durante o movimento e reaparecem já no lugar novo.
    const conteudo = cards.flatMap((card) => [...card.querySelectorAll('.imovel-card__corpo, .imovel-card__lateral')]);
    gsap.fromTo(conteudo, { opacity: 0 }, {
        opacity: 1,
        duration: 0.4,
        delay: 0.4,
        stagger: 0.015,
        clearProps: 'opacity',
    });
}

// -----------------------------------------------------------------------------------------------

export default {
    init() {
        if (reduzido()) {
            document.documentElement.classList.remove('motion-ativo');
            return;
        }
        window.niMotionPronto = true;
        window.niMotion = { trocarLayout };
        try {
            window.niLenis = iniciarLenis();
            aplicarAutomatico();
            iniciarHero();
            iniciarRevelacoes();
            iniciarCards();
            iniciarPlaceholders();
            // Fontes carregadas mudam alturas: recalcula os pontos de disparo.
            if (document.fonts && document.fonts.ready) {
                document.fonts.ready.then(() => ScrollTrigger.refresh());
            }
        } catch (erro) {
            // Qualquer falha não pode deixar conteúdo escondido.
            document.documentElement.classList.remove('motion-ativo');
            throw erro;
        }
    },
};
